// Google Ads tagging: Consent Mode v2, virtual page views and the single "Book appointment"
// conversion. The tag ID is a public identifier, so it lives in index.html; the conversion
// label comes from the environment so the exact Google Ads value can be set without code changes.
export const ADS_ID = "AW-18161154624";
export const CONVERSION_LABEL = (process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "").trim();

export const CONSENT_KEY = "pg_cookie_consent_v1";
const CLICK_ID_KEY = "pg_ad_click_ids_v1";
const FIRED_KEY = "pg_booking_conversion_fired_v1";
const CLICK_ID_PARAMS = ["gclid", "gbraid", "wbraid"];

export function gtag(...args) {
  window.dataLayer = window.dataLayer || [];
  // The global defined in index.html pushes a genuine `arguments` object, which is what
  // gtag.js expects. Falling back keeps the app safe if the tag is blocked or removed.
  if (typeof window.gtag === "function") {
    window.gtag(...args);
    return;
  }
  window.dataLayer.push(args);
}

function read(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private browsing — the tag simply stays at its denied default */
  }
}

// --- consent ---------------------------------------------------------------
export function getConsent() {
  const stored = read(CONSENT_KEY);
  if (!stored || typeof stored.granted !== "boolean") return null;
  return stored;
}

/** Tells Google what the visitor chose. Everything stays denied until they accept. */
export function applyConsent(granted) {
  const state = granted ? "granted" : "denied";
  gtag("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
}

export function saveConsent(granted) {
  write(CONSENT_KEY, { granted, at: new Date().toISOString(), version: 1 });
  applyConsent(granted);
  window.dispatchEvent(new CustomEvent("pg:consent-changed", { detail: { granted } }));
}

export function clearConsent() {
  try {
    window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* nothing to clear */
  }
  applyConsent(false);
}

// --- Google Ads click identifiers -----------------------------------------
/** Keeps gclid/gbraid/wbraid with the visitor across the booking journey. */
export function captureClickIds(search = window.location.search) {
  const params = new URLSearchParams(search);
  const found = {};
  CLICK_ID_PARAMS.forEach((name) => {
    const value = params.get(name);
    if (value) found[name] = value;
  });
  if (Object.keys(found).length) {
    write(CLICK_ID_KEY, { ...(read(CLICK_ID_KEY) || {}), ...found, at: new Date().toISOString() });
  }
  return read(CLICK_ID_KEY) || {};
}

export function getClickIds() {
  const stored = read(CLICK_ID_KEY) || {};
  return CLICK_ID_PARAMS.reduce((out, name) => {
    if (stored[name]) out[name] = stored[name];
    return out;
  }, {});
}

// --- page views -----------------------------------------------------------
let lastPath = null;

/** One page_view per view. `send_page_view` is off in index.html so nothing is duplicated. */
export function trackPageView(path) {
  if (path === lastPath) return;
  lastPath = path;
  gtag("event", "page_view", {
    send_to: ADS_ID,
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_title: document.title,
  });
}

// --- the booking conversion ----------------------------------------------
function alreadyFired(reference) {
  const fired = read(FIRED_KEY);
  return Array.isArray(fired) && fired.includes(reference);
}

function rememberFired(reference) {
  const fired = read(FIRED_KEY);
  const list = Array.isArray(fired) ? fired : [];
  write(FIRED_KEY, [...new Set([...list, reference])].slice(-50));
}

/**
 * Sends the "Book appointment" conversion. Only ever called with a confirmation the backend
 * has already granted (it decides, once, whether this booking may be counted). The
 * localStorage check below is a second belt against refreshes and re-renders.
 */
export function trackBookingConversion({ reference, value, currency = "GBP" }) {
  if (!reference) return { fired: false, reason: "no booking reference" };
  if (!CONVERSION_LABEL) return { fired: false, reason: "conversion label not configured" };
  if (alreadyFired(reference)) return { fired: false, reason: "already counted in this browser" };
  rememberFired(reference);
  gtag("event", "conversion", {
    send_to: `${ADS_ID}/${CONVERSION_LABEL}`,
    value: Number(value) || 0,
    currency,
    transaction_id: reference,
  });
  return { fired: true };
}
